import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Fuse from 'fuse.js';

// Funktion zum Entfernen aller Markierungen
const removeAllHighlights = () => {
  const existingHighlights = document.querySelectorAll('.search-highlight');
  existingHighlights.forEach(el => {
    const parent = el.parentNode;
    if (parent) {
      parent.replaceChild(document.createTextNode(el.textContent || ''), el);
      parent.normalize();
    }
  });
};

export const useSearchHighlight = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const sectionId = searchParams.get('section');
    const highlightTerm = searchParams.get('highlight');

    if (sectionId || highlightTerm) {
      // Wait for page to render
      setTimeout(() => {
        // Scroll to section if provided
        if (sectionId) {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        
        // Always highlight on entire page, regardless of section
        if (highlightTerm) {
          setTimeout(() => {
            highlightTextInElement(document.body, highlightTerm);
          }, 300);
        }

        // Clean up URL parameters after highlighting
        setTimeout(() => {
          searchParams.delete('section');
          searchParams.delete('highlight');
          setSearchParams(searchParams, { replace: true });
        }, 1000);
      }, 100);
    }
  }, [searchParams, setSearchParams]);

  // ESC-Taste zum Entfernen der Markierungen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Nur ESC verarbeiten, wenn keine Popups/Modals offen sind
      if (e.key === 'Escape') {
        // Prüfe ob ein Popup/Modal offen ist (durch Prüfung auf z-index und visibility)
        const hasOpenPopup = 
          document.querySelector('[data-text-to-speech-player]') ||
          // Prüfe auf Overlays mit hohem z-index (SearchPalette, AccessibilityPanel, CookieManager)
          Array.from(document.querySelectorAll('[style*="z-index"]')).some(el => {
            const style = window.getComputedStyle(el);
            const zIndex = parseInt(style.zIndex) || 0;
            const isVisible = style.display !== 'none' && 
                             style.visibility !== 'hidden' && 
                             parseFloat(style.opacity) > 0;
            return zIndex >= 10000 && isVisible;
          });
        
        // Wenn kein Popup offen ist, entferne Markierungen
        if (!hasOpenPopup) {
          const hasHighlights = document.querySelectorAll('.search-highlight').length > 0;
          if (hasHighlights) {
            e.preventDefault();
            removeAllHighlights();
          }
        }
      }
    };

    // Verwende normale Phase (nicht capture), damit Popups Vorrang haben
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
};

const highlightTextInElement = (container: HTMLElement, searchTerm: string) => {
  // Remove previous highlights (verwende die gemeinsame Funktion)
  removeAllHighlights();

  // Find and highlight all instances (fuzzy-aware)
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        // Skip script and style tags
        const parent = node.parentElement;
        if (parent && (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE')) {
          return NodeFilter.FILTER_REJECT;
        }
        
        const text = node.textContent || '';
        if (!text.trim()) return NodeFilter.FILTER_SKIP;
        
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );

  const nodesToReplace: { node: Text; matches: { index: number; length: number }[] }[] = [];
  let node: Text | null;

  // Collect all text nodes and find matches
  while ((node = walker.nextNode() as Text | null)) {
    const text = node.textContent || '';
    const matches = findFuzzyMatches(text, searchTerm);
    
    if (matches.length > 0) {
      nodesToReplace.push({ node, matches });
    }
  }

  // Replace nodes with highlighted version
  nodesToReplace.forEach(({ node, matches }) => {
    const text = node.textContent || '';
    const fragment = document.createDocumentFragment();
    
    let lastIndex = 0;
    
    matches.forEach(match => {
      // Add text before match
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
      }
      
      // Add highlighted match - türkiser Hintergrund
      const mark = document.createElement('mark');
      mark.className = 'search-highlight';
      mark.style.backgroundColor = 'rgba(0, 217, 255, 0.2)';
      mark.style.color = '#111827';
      mark.style.padding = '2px 4px';
      mark.style.borderRadius = '3px';
      mark.style.fontWeight = '600';
      mark.textContent = text.substring(match.index, match.index + match.length);
      fragment.appendChild(mark);
      
      lastIndex = match.index + match.length;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }
    
    node.parentNode?.replaceChild(fragment, node);
  });

  // Scroll to first highlight
  setTimeout(() => {
    const firstHighlight = document.querySelector('.search-highlight');
    if (firstHighlight) {
      firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 100);
};

// Helper function to find fuzzy matches in text
const findFuzzyMatches = (text: string, searchTerm: string): { index: number; length: number }[] => {
  const lowerText = text.toLowerCase();
  const matches: { index: number; length: number }[] = [];
  
  // Support multiple search terms separated by |
  const searchTerms = searchTerm.split('|').map(t => t.trim()).filter(t => t);
  
  searchTerms.forEach(term => {
    const lowerTerm = term.toLowerCase();
    let termMatches: { index: number; length: number }[] = [];
    
    // First try exact match (case-insensitive)
    let index = lowerText.indexOf(lowerTerm);
    while (index !== -1) {
      // Use actual text length at this position (not search term length)
      const actualMatch = text.substring(index, index + term.length);
      termMatches.push({ index, length: actualMatch.length });
      index = lowerText.indexOf(lowerTerm, index + 1);
    }
    
    // For phone numbers and emails, also try matching ignoring whitespace differences
    if (termMatches.length === 0 && (term.includes('+') || term.includes('@'))) {
      // Try regex match which is more flexible
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedTerm.replace(/\s+/g, '\\s*'), 'gi');
      let match;
      
      while ((match = regex.exec(text)) !== null) {
        termMatches.push({ index: match.index, length: match[0].length });
      }
    }
    
    // If no exact matches, try fuzzy matching on words
    // This finds words that are similar to the search term (e.g., "Yooga" → "Yoga")
    if (termMatches.length === 0 && term.length >= 3) {
      // Split text into words while preserving positions
      const wordMatches: Array<{ word: string; index: number; length: number }> = [];
      const wordRegex = /\b\w+\b/g;
      let match;
      
      while ((match = wordRegex.exec(text)) !== null) {
        const word = match[0];
        if (word.length >= 3) {
          wordMatches.push({
            word: word,
            index: match.index,
            length: word.length
          });
        }
      }
      
      // Use Fuse.js to find similar words
      if (wordMatches.length > 0) {
        const fuse = new Fuse(wordMatches, {
          keys: ['word'],
          threshold: 0.4, // Slightly more lenient for typos
          minMatchCharLength: 3,
          includeScore: true,
          ignoreLocation: true
        });
        
        const fuseResults = fuse.search(term);
        
        // Add all fuzzy matches - use the actual word from text, not the search term
        fuseResults.forEach(result => {
          const matchedWord = result.item;
          termMatches.push({
            index: matchedWord.index,
            length: matchedWord.length // Use actual word length, not search term length
          });
        });
      }
    }
    
    // Add term matches to overall matches
    matches.push(...termMatches);
  });
  
  return matches;
};

